/**
 * ComposeExpertSearch Component
 * Gmail-style autocomplete search for experts with inline badges
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { TYPOGRAPHY } from '@/design-system';
import { useLocalizations } from '@/hooks/useLocalizations';
import { LComponents } from '@/components/i18n';
import { Badge } from '@/components/ui/badge';
import { ExpertSearchResultSkeleton } from '@/components/skeletons';
import { User, History, MapPin, X } from 'lucide-react';
import { VerifiedBadge } from '@/components/shared/VerifiedBadge';
import { countries } from '@/data/countries';
import { toast } from 'sonner';
import { useLocationById } from '@/hooks/useLocationById';
import type { Expert } from './types';
import { getExpertDisplayName } from './types';

interface ComposeExpertSearchProps {
  experts: Expert[] | undefined;
  expertsLoading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedExperts: Expert[];
  onAddExpert: (expert: Expert) => void;
  onRemoveExpert: (expertId: string) => void;
  lockedRecipients?: boolean;
  disabled?: boolean;
  recentContacts?: Expert[];
  locationMatches?: Expert[];
  locationMatchesMode?: 'top_experts' | 'near_you';
}

export function ComposeExpertSearch({
  experts,
  expertsLoading,
  searchQuery,
  onSearchChange,
  selectedExperts,
  onAddExpert,
  onRemoveExpert,
  lockedRecipients,
  disabled,
  recentContacts,
  locationMatches,
  locationMatchesMode,
}: ComposeExpertSearchProps) {
  const { lComponents, lCommon } = useLocalizations();
  const [isFocused, setIsFocused] = useState(false);

  const handleSelectExpert = (expert: Expert) => {
    if (selectedExperts.length >= 3) {
      toast.warning(
        lComponents('compose_message.max_experts_warning', { 
          defaultValue: 'You can select up to 3 experts. Remove one to add another.' 
        })
      );
      return;
    }
    onAddExpert(expert);
    onSearchChange('');
  };

  // Filter out already selected experts
  const filterSelected = (list: Expert[] | undefined) => 
    list?.filter(e => !selectedExperts.find(s => s.id === e.id)) || [];

  const filteredRecent = filterSelected(recentContacts);
  const filteredLocation = filterSelected(locationMatches)
    .filter(e => !filteredRecent.find(r => r.id === e.id));

  const hasQuery = searchQuery.trim().length > 0;
  const hasSuggestions = filteredRecent.length > 0 || filteredLocation.length > 0;
  const canAddMore = selectedExperts.length < 3;
  const showDropdown = isFocused && !disabled && canAddMore && (hasQuery || hasSuggestions);

  const ExpertRow = ({ expert }: { expert: Expert }) => {
    // Per project memory (jurisdictional-identity-source-of-truth):
    // location_id is the SOT — resolve flag/country from it first, fall back to legacy column.
    const { isoAlpha2: resolvedIso, name: resolvedCountryName } = useLocationById(expert.location_id);
    const effectiveCountryCode = resolvedIso || expert.country || null;
    const countryInfo = effectiveCountryCode
      ? countries.find(c => c.code === effectiveCountryCode)
      : undefined;
    const countryDisplayName = resolvedCountryName || countryInfo?.name || effectiveCountryCode || null;
    return (
      <button
        type="button"
        onClick={() => handleSelectExpert(expert)}
        className="w-full px-3 py-2 text-left hover:bg-accent focus:bg-accent focus:outline-none transition-colors cursor-pointer flex items-center gap-3"
      >
        <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
          {expert.image ? (
            <img 
              src={expert.image} 
              alt={getExpertDisplayName(expert)} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs font-bold text-muted-foreground">
              {(getExpertDisplayName(expert) || 'E')[0].toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className={cn(TYPOGRAPHY.cardTitleSmall, 'truncate')}>
              {getExpertDisplayName(expert) || lCommon('labels.expert', { defaultValue: 'Expert' })}
            </p>
            {expert.membership_tier != null && (
              <VerifiedBadge size={12} displayName={getExpertDisplayName(expert)} membershipTier={expert.membership_tier} />
            )}
          </div>
          <p className={cn(TYPOGRAPHY.caption, 'truncate')}>
            {expert.city && countryDisplayName 
              ? `${expert.city}, ${countryDisplayName}`
              : expert.city || countryDisplayName || lComponents('compose_message.location_not_specified', { defaultValue: 'Location Not Specified' })}
            {countryInfo && ` ${countryInfo.flag}`}
          </p>
        </div>
      </button>
    );
  };

  const SelectedExpertBadge = ({ expert }: { expert: Expert }) => {
    const { isoAlpha2: resolvedIso } = useLocationById(expert.location_id);
    const effectiveCountryCode = resolvedIso || expert.country || null;
    const countryInfo = effectiveCountryCode
      ? countries.find(c => c.code === effectiveCountryCode)
      : undefined;
    return (
      <Badge variant="secondary" className="gap-1 px-2 py-0.5 h-6">
        <div className="w-4 h-4 rounded-full overflow-hidden bg-muted flex-shrink-0">
          {expert.image ? (
            <img 
              src={expert.image} 
              alt="" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[8px] font-bold text-muted-foreground">
              {(getExpertDisplayName(expert) || 'E')[0].toUpperCase()}
            </div>
          )}
        </div>
        <span className="text-xs truncate max-w-[80px]">
          {getExpertDisplayName(expert) || lCommon('labels.expert', { defaultValue: 'Expert' })}
        </span>
        {countryInfo && <span className="text-[10px]">{countryInfo.flag}</span>}
        {!lockedRecipients && (
          <X 
            className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" 
            onClick={(e) => {
              e.stopPropagation();
              onRemoveExpert(expert.id);
            }}
          />
        )}
      </Badge>
    );
  };

  return (
    <div className="relative">
      {/* Gmail-style input container with inline badges */}
      <div
        className={cn(
          "flex items-center gap-1.5 h-10 px-3 overflow-x-auto",
          "border border-input rounded-md bg-background input-interactive",
          "focus-within:outline-none focus-within:border-ring",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {/* Inline selected badges */}
        {selectedExperts.map((expert) => (
          <SelectedExpertBadge key={expert.id} expert={expert} />
        ))}

        {/* Inline search input - always visible for interaction */}
        <input
          type="text"
          placeholder={selectedExperts.length === 0 
            ? lComponents('compose_message.placeholders.search_experts', { defaultValue: 'Search experts' }) 
            : canAddMore 
            ? '' 
            : lComponents('compose_message.max_experts_reached', { defaultValue: 'Max 3 experts' })}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          disabled={disabled || !canAddMore}
          className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground"
        />
      </div>
      {/* Dropdown - Shows suggestions when focused, search results when typing */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 z-[200] mt-1">
          <div className="border rounded-lg bg-background shadow-lg max-h-[300px] overflow-y-auto">
            {/* Show inline suggestions when not searching */}
            {!hasQuery && (
              <>
                {filteredRecent.length > 0 && (
                  <div className="py-1">
                    <div className="px-3 py-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <History className="h-3 w-3" />
                      <LComponents id="compose_message.labels.recent_contacts" defaultValue='Recent Contacts' />
                    </div>
                    {filteredRecent.slice(0, 3).map((expert) => (
                      <ExpertRow key={expert.id} expert={expert} />
                    ))}
                  </div>
                )}
                
                {filteredLocation.length > 0 && (
                  <div className={cn("py-1", filteredRecent.length > 0 && "border-t")}>
                    <div className="px-3 py-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {locationMatchesMode === 'top_experts' ? (
                        <LComponents id="compose_message.labels.top_experts" defaultValue="Top experts" />
                      ) : (
                        <LComponents id="compose_message.labels.experts_near_you" defaultValue="Near you" />
                      )}
                    </div>
                    {filteredLocation.slice(0, 3).map((expert) => (
                      <ExpertRow key={expert.id} expert={expert} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Show search results when typing */}
            {hasQuery && (
              <>
                {expertsLoading ? (
                  <div className="p-2">
                    <ExpertSearchResultSkeleton items={3} />
                  </div>
                ) : experts && experts.length > 0 ? (
                  <div className="py-1">
                    {experts
                      .filter(e => !selectedExperts.find(s => s.id === e.id))
                      .slice(0, 10)
                      .map((expert) => (
                        <ExpertRow key={expert.id} expert={expert} />
                      ))}
                  </div>
                ) : (
                  <div className="py-6 text-center">
                    <User className="h-10 w-10 text-muted-foreground mx-auto mb-2 opacity-50" />
                    <p className={TYPOGRAPHY.cardDescription}>
                      <LComponents
                        id="compose_message.no_experts_found.description"
                        defaultValue='No experts were found matching your criteria. Please adjust your search.'
                      />
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
